import React, { CSSProperties, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Button, Icon, Image, Input, InputOnChangeData, List, Popup } from 'semantic-ui-react'
import order from '../../../../assets/svg/order.svg'
import plus_white from '../../../../assets/svg/plus_white.svg'
import search from '../../../../assets/svg/search_icon.svg'
import { ContentHeader } from '../../../../components/Header/ContentHeader'
import { useWindowSize } from '../../../../hooks/useWindowSize'
import { RootState } from '../../../../store/rootState'
import { IUserDetail } from '../../../../types/user'
import { checkRole } from '../../../../utils/checkRole'
import * as ProjectsActions from '../../actions/projectsActions'
import * as WizardActions from '../../actions/wizardActions'
import { IProjectFilters, IProjectTypeFilter } from '../../types/project'
import ProjectCreateWizardContainer from './ProjectCreateWizardContainer'

interface StateProps {
  filters: IProjectFilters
  wizardStep: string
  user: IUserDetail | null
  projectTypeFilters: IProjectTypeFilter[]
  projectTypeFilter: IProjectTypeFilter
  projectSorting: { name: string, direction: string }
}

interface DispatchProps {
  setWizardStep: (step: string) => void
  filterProjects: (filters: IProjectFilters) => void
  getProjects: (queryParams?: IProjectFilters) => void
  setProjectTypeFilter: (filter: IProjectTypeFilter) => void
  sortProjects: (sort: { name: string, direction: string}) => void
}

type Props = DispatchProps & StateProps


const projectOrderOptions = [
  { key: 1, name: 'Name', value: 'name' },
  { key: 2, name: 'Budget', value: 'budget' },
  { key: 3, name: 'Type', value: 'type' },
  { key: 4, name: 'Start Date', value: 'start_date' },
  { key: 5, name: 'End Date', value: 'end_date' },
  { key: 6, name: 'Health', value: 'health' },
]

const badgeStyle: CSSProperties = {
  borderRadius: '7.2px',
  height: '2rem',
  fontSize: '1rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer'
}

const ProjectListHeaderContainer = (props: Props) => {
  const {
    filters, wizardStep, getProjects,
    setWizardStep, filterProjects,
    setProjectTypeFilter, user,
    projectTypeFilters, projectTypeFilter,
    sortProjects, projectSorting } = props

  const [searchLoading, setSearchLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [timer, setTimer] = useState<number>(0)
  const [orderOpts, setOrderOpts] = useState(projectOrderOptions)

  const windowWidth = useWindowSize().width

  useEffect(() => {
    return () => {
      setSearchQuery('')
      filterProjects({search: ''})
      sortProjects({name: 'name', direction: 'asc'})
    }
  }, [sortProjects, filterProjects])

  useEffect(() => {
    if (projectTypeFilter.key === 'all' || projectTypeFilter.key === 'archive') {
      setOrderOpts([
        { key: 1, name: 'Name', value: 'name' },
        { key: 4, name: 'Start Date', value: 'start_date' },
        { key: 5, name: 'End Date', value: 'end_date' },
      ])
    } else if (projectTypeFilter.key === 'internal') {
      setOrderOpts(projectOrderOptions.filter(o => o.value !== 'health' && o.value !== 'budget'))
    } else if (projectTypeFilter.key === 'sponsor') {
      setOrderOpts(projectOrderOptions)
    } else {
      setOrderOpts(projectOrderOptions.map(opts => {
        if (opts.value === 'budget') {
          return {...opts, name: 'Direct Costs'}
        }
        return opts
      }))
    }

  }, [projectTypeFilter.key])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
    clearTimeout(timer)
    const _timer = window.setTimeout(() => {
      setSearchLoading(false)
    }, 300)
    filterProjects({...filters, search: data.value})
    setTimer(_timer)
    setSearchLoading(true)
    setSearchQuery(data.value)
  }

  const handleClearSearch = () => {
    filterProjects({...filters, search: ''})
    setSearchQuery('')
  }

  const renderSearchIcon = () => {
    if (searchQuery === '') {
      return <Image src={search} style={{ left: '-100%', top: '-3%', padding: '0.5rem' }} />
    }
    return <Icon name='close' link={true} onClick={handleClearSearch} />
  }

  const showModal = () => {
    setWizardStep('step-one')
  }

  const handleTypeClicked = (type: IProjectTypeFilter) => {
    setProjectTypeFilter({...type})
  }

  const leftItems = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '1.5rem', width: '100%', gap: '8px' }}>
        {projectTypeFilters.map(l => {
          if (l.key === 'archive' && user && !checkRole(user, [12])) {
            return
          }
          return (
            <div
              onClick={() => handleTypeClicked(l)}
              style={{...badgeStyle, color: l.key !== projectTypeFilter.key ? '#747678' : '#232C58', background: l.key === projectTypeFilter.key? '#1a232c24' : '', padding: '0 12px', whiteSpace: 'nowrap'}}
              key={l.key}>
                {l.name} {l.count !== undefined ? `(${l.count})` : ''}
              </div>
          )
        })}
      </div>
    )
  }

  const renderFilterDropdown = () => {
    return (
      <List>
        {orderOpts.map(order => {
          const click = order.value.toLowerCase()
          return (
            <List.Item key={order.key} onClick={() => handleOrderingSelect(order.value)} style={{ display: 'flex', cursor: 'pointer' }} >
              <div style={{ marginRight: '5px' }}>{order.name}</div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Icon name='sort up' style={{ opacity: projectSorting.name === click && projectSorting.direction === 'asc' ? .4 : 1 }} />
                <Icon name='sort down' style={{ marginTop: '-1rem', opacity: projectSorting.name === click && projectSorting.direction === 'desc' ? .4 : 1 }} />
              </div>
            </List.Item>
          )
        })}
      </List>
    )
  }


  const handleOrderingSelect = (_order: string) => {
    const _projectSorting = {...projectSorting}
    if (_order === projectSorting.name) {
      if (projectSorting.direction === 'asc') {
        _projectSorting.direction = 'desc'
      } else {
        _projectSorting.direction = 'asc'
      }
    } else {
      _projectSorting.name = _order
      _projectSorting.direction = 'asc'
    }
    sortProjects(_projectSorting)
  }

  const rightItems = () => {
    return (
      <React.Fragment>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Input
            loading={searchLoading}
            className='search-input-medium'
            size='small'
            style={{ width: '170px' }}
            icon={renderSearchIcon()}
            iconPosition='left'
            placeholder='Search'
            onChange={(e, data) => handleSearch(e, data)}
            value={searchQuery}
          />
          <Popup
            basic
            trigger={
              <Button
                size='small'
                secondary={true}
                content={<Image src={order} />}
                style={{ height: '30px', width: '32px', padding: 0, borderRadius: '8px', margin: 0, backgroundColor: '#232C58' }}
              />
            }
            content={renderFilterDropdown()}
            on='click'
            position='bottom left'
          />
        {user && checkRole(user, [7, 8, 9]) ?
          <Button
            primary={true}
            content={windowWidth < 1130 ? '' : 'New Project'}
            onClick={showModal}
            icon={<Image src={plus_white} style={{ height: '16px', marginRight: '5px'}} />}
            style={{
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              height: '30px',
              width: windowWidth < 1130 ? '32px' : '',
              borderRadius: '6px',
              padding: windowWidth < 1130 ? '0 6px 0 8px' : '12px',
              margin: 0
            }}
          /> : null}
        </div>
      </React.Fragment>
    )
  }

  return (
    <div id='projectListHeader'>
      <ContentHeader title='Project List' color='#232C58' leftItems={leftItems()} rightItemsFirst={rightItems()} />
      {wizardStep !== '' ? <ProjectCreateWizardContainer /> : null}
    </div>
  )
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    filters: state.projectsState.filterProjects,
    wizardStep: state.projectsState.wizardApp.wizardStep,
    user: state.authState.user,
    projectTypeFilters: state.projectsState.projectTypeFilters,
    projectTypeFilter: state.projectsState.projectTypeFilter,
    projectSorting: state.projectsState.projectSort
  }
}

const mapDispatchToProps = (
  dispatch: WizardActions.WizadThunkDispatchType & ProjectsActions.ProjectsThunkDispatchType
): DispatchProps => {
  return {
    setWizardStep: (step: string) => dispatch(WizardActions.setWizardStep(step)),
    filterProjects: filters => dispatch(ProjectsActions.filterProjects(filters)),
    getProjects: async queryParams => dispatch(ProjectsActions.getProjects(queryParams)),
    setProjectTypeFilter: filter => dispatch(ProjectsActions.setProjectTypeFilter(filter)),
    sortProjects: sort => dispatch(ProjectsActions.sortProjects(sort))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectListHeaderContainer)
